import { useState, useEffect } from "react";
import { useAuth } from "../lib/auth";
import { referralAPI } from "../lib/api";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Copy, Gift, Users, Crown, Check, Clock, Share2, UserPlus, IndianRupee } from "lucide-react";

const REWARD_RULES = [
  { icon: UserPlus, trigger: "Friend signs up + active 3 days (1+ client)", reward: "+7 days Pro FREE", color: "text-primary" },
  { icon: Crown, trigger: "Friend buys 1 month Pro/Elite", reward: "+7 days same plan FREE", color: "text-green-400" },
  { icon: Crown, trigger: "Friend buys 3+ months Pro/Elite", reward: "+1 month same plan FREE", color: "text-amber-400" },
];

export default function InviteEarnPage() {
  const { trainer } = useAuth();
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [statsRes, refsRes] = await Promise.all([
          referralAPI.getStats(),
          referralAPI.getMyReferrals(),
        ]);
        setStats(statsRes.data);
        setReferrals(refsRes.data || []);
      } catch (err) {
        toast.error("Failed to load referral data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleCopyLink = () => {
    if (stats?.invite_url) {
      navigator.clipboard.writeText(stats.invite_url);
      toast.success("Invite link copied!");
    }
  };

  const handleShare = async () => {
    if (!stats?.invite_url) return;
    const text = `Hey! I use FitTrack Coach to manage my gym clients. It's free to start — try it out: ${stats.invite_url}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "FitTrack Coach", text, url: stats.invite_url });
      } catch {}
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Share text copied!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header title="Invite & Earn" subtitle="Referral Rewards" />
        <div className="flex items-center justify-center py-20"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" data-testid="invite-earn-page">
      <Header title="Invite & Earn" subtitle="Referral Rewards" />

      <main className="container mx-auto px-4 py-6 max-w-2xl space-y-6">

        {/* Hero Card */}
        <Card className="glass-card border-0 overflow-hidden relative" data-testid="invite-hero">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-green-400 to-amber-400" />
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Gift className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-foreground mb-2">
              Invite Friends, Earn Pro Free
            </h2>
            <p className="text-sm text-muted-foreground mb-5 max-w-sm mx-auto">
              Share your link with fellow trainers. When they join & subscribe, you get free Pro/Elite access!
            </p>

            {/* Invite Link */}
            <div className="flex items-center gap-2 p-3 bg-white/[0.03] border border-white/[0.08] rounded-xl mb-4">
              <input
                readOnly value={stats?.invite_url || "Loading..."}
                className="flex-1 bg-transparent text-sm text-foreground font-mono truncate outline-none"
                data-testid="invite-link-input"
              />
              <Button size="sm" variant="ghost" onClick={handleCopyLink} className="shrink-0 text-primary" data-testid="copy-link-btn">
                <Copy className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex gap-2">
              <Button className="flex-1 h-11 btn-premium text-white font-bold uppercase" onClick={handleShare} data-testid="share-invite-btn">
                <Share2 className="w-4 h-4 mr-2" /> Share Link
              </Button>
              <Button variant="outline" className="h-11 border-primary/30 text-primary" onClick={handleCopyLink}>
                <Copy className="w-4 h-4 mr-2" /> Copy
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="glass-card border-0">
            <CardContent className="p-4 text-center">
              <Users className="w-5 h-5 text-primary mx-auto mb-1" />
              <p className="text-2xl font-extrabold text-foreground">{stats?.total_invited || 0}</p>
              <p className="text-[10px] text-muted-foreground uppercase font-bold">Invited</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-0">
            <CardContent className="p-4 text-center">
              <Check className="w-5 h-5 text-green-400 mx-auto mb-1" />
              <p className="text-2xl font-extrabold text-green-400">{stats?.active_referrals || 0}</p>
              <p className="text-[10px] text-muted-foreground uppercase font-bold">Active</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-0">
            <CardContent className="p-4 text-center">
              <Crown className="w-5 h-5 text-amber-400 mx-auto mb-1" />
              <p className="text-2xl font-extrabold text-amber-400">{stats?.total_reward_days || 0}</p>
              <p className="text-[10px] text-muted-foreground uppercase font-bold">Days Earned</p>
            </CardContent>
          </Card>
        </div>

        {/* Reward Rules */}
        <Card className="glass-card border-0" data-testid="reward-rules">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base tracking-wide uppercase">How It Works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {REWARD_RULES.map((rule, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-white/[0.02] rounded-xl border border-white/[0.04]">
                <div className={`w-9 h-9 rounded-lg bg-white/[0.04] flex items-center justify-center shrink-0 ${rule.color}`}>
                  <rule.icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">{rule.trigger}</p>
                  <p className={`text-sm font-bold ${rule.color}`}>{rule.reward}</p>
                </div>
              </div>
            ))}
            <p className="text-[10px] text-muted-foreground/70 text-center mt-2 italic">
              * "Active" means friend must be on the app for 3+ days & have added at least 1 client
            </p>
          </CardContent>
        </Card>

        {/* Rewards History */}
        {stats?.rewards?.length > 0 && (
          <Card className="glass-card border-0" data-testid="rewards-history">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base tracking-wide uppercase">Rewards Earned</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {stats.rewards.map((r, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-white/[0.02] rounded-xl border border-white/[0.04]">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <Gift className="w-4 h-4 text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{r.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">+{r.reward_days} days</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Referrals List */}
        <Card className="glass-card border-0" data-testid="referrals-list">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base tracking-wide uppercase">
              Your Invites ({referrals.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {referrals.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No invites yet</p>
                <p className="text-xs text-muted-foreground/60 mt-1">Share your link to start earning rewards</p>
              </div>
            ) : (
              <div className="space-y-2">
                {referrals.map((ref) => (
                  <div key={ref.id} className="flex items-center justify-between p-3 bg-white/[0.02] rounded-xl border border-white/[0.04]">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${
                        ref.has_paid ? "bg-green-500/20 text-green-400" :
                        ref.is_active_3days ? "bg-primary/20 text-primary" :
                        "bg-white/[0.05] text-muted-foreground"
                      }`}>
                        {ref.name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{ref.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {ref.client_count || 0} clients · {ref.days_active} days
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {ref.has_paid ? (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px]">
                          <IndianRupee className="w-3 h-3 mr-0.5" /> Paid ({ref.plan_type})
                        </Badge>
                      ) : ref.is_active_3days ? (
                        <Badge className="bg-primary/20 text-primary border-primary/30 text-[10px]">Active</Badge>
                      ) : (
                        <Badge className="bg-white/[0.05] text-muted-foreground border-white/[0.06] text-[10px]">
                          <Clock className="w-3 h-3 mr-0.5" /> Pending
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center pt-4">
          <p className="text-xs text-muted-foreground">
            Tracked with <span className="text-primary font-medium">FitTrack Coach</span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            <a href="https://app.fittrackcoach.in" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">app.fittrackcoach.in</a>
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">by InfiNest Tech Pvt. Ltd.</p>
        </div>
      </main>
    </div>
  );
}
